import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { getAgents, updateAgentStatus } from "../../api/agents";
import { InputField } from "../../components/Input";
import { PrimaryButton, OutlineButton } from "../../components/Button";
import { SearchFilterContainer } from "../../components/SearchFilterTable";
import useMessageModal from "../../hooks/useMessageModal.hook";

const STATUS_LABELS = {
  pending_approval: "Pending Approval",
  active: "Active",
  disabled: "Disabled",
  suspended: "Suspended",
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "disabled", label: "Disabled" },
];

function statusColorClass(status) {
  if (status === "active") return "color-primary";
  if (status === "pending_approval") return "color-orange";
  if (status === "suspended") return "tart-orange";
  if (status === "disabled") return "charcoal-60";
  return "charcoal-80";
}

function safeLower(v) {
  return String(v ?? "").toLowerCase();
}

export default function AgentsManagement() {
  const [setPageTitle] = useOutletContext();
  setPageTitle("Agents");

  const showMessageModal = useMessageModal();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");

  const [reason, setReason] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [loading, setLoading] = useState(true);
  const [busyAgentId, setBusyAgentId] = useState("");
  const [pageData, setPageData] = useState({
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
  });

  const [loadError, setLoadError] = useState("");

  const toastError = (error, fallback) => {
    showMessageModal({
      title: "Error",
      message: error?.response?.data?.errorMessage || error?.message || fallback,
      isError: true,
      closeButtonText: "Close",
    });
  };

  const toastSuccess = (message) => {
    showMessageModal({
      title: "Success",
      message,
      isError: false,
      closeButtonText: "Close",
    });
  };

  async function load() {
    setLoading(true);
    setLoadError("");
    try {
      const data = await getAgents({
        status: statusFilter || undefined,
        limit,
        offset,
      });

      setPageData({
        items: Array.isArray(data?.items) ? data.items : [],
        total: Number(data?.total ?? 0),
        limit: Number(data?.limit ?? limit),
        offset: Number(data?.offset ?? offset),
      });
    } catch (e) {
      console.error(e);
      setLoadError(e?.response?.data?.errorMessage || e?.message || "Failed to load agents.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, limit, offset]);

  const agents = pageData.items;

  const filteredAgents = useMemo(() => {
    const q = safeLower(search).trim();
    if (!q) return agents;

    return agents.filter((a) => {
      const name = safeLower(`${a?.firstName || ""} ${a?.lastName || ""}`.trim());
      const phone = safeLower(a?.phoneNo);
      const email = safeLower(a?.email);
      const status = safeLower(a?.status);
      return name.includes(q) || phone.includes(q) || email.includes(q) || status.includes(q);
    });
  }, [agents, search]);

  async function doStatusUpdate(agent, action) {
    const agentId = agent?.id;
    if (!agentId) {
      toastError(null, "Cannot update agent: missing agent id.");
      return;
    }

    setBusyAgentId(agentId);
    try {
      await updateAgentStatus({
        agentId,
        action, // approve | suspend | unsuspend
        reason: reason?.trim() ? reason.trim() : undefined,
      });

      toastSuccess("Agent status updated.");
      setReason("");
      await load();
    } catch (e) {
      console.error(e);
      toastError(e, "Failed to update agent status.");
    } finally {
      setBusyAgentId("");
    }
  }

  const page = Math.floor(pageData.offset / pageData.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(pageData.total / pageData.limit));
  const canPrev = pageData.offset > 0;
  const canNext = pageData.offset + pageData.limit < pageData.total;

  const reloadTable = ({ search: s }) => {
    setSearch(s || "");
  };

  const prependWidget = (
    <div className="flex center gap-12">
      <div style={{ minWidth: 220 }}>
        <select
          value={statusFilter}
          onChange={(e) => {
            setOffset(0);
            setStatusFilter(e.target.value);
          }}
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ minWidth: 150 }}>
        <select
          value={limit}
          onChange={(e) => {
            setOffset(0);
            setLimit(Number(e.target.value));
          }}
        >
          {[25, 50, 100, 200].map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
      </div>

      <OutlineButton className="mini" onClick={load} disabled={loading}>
        {loading ? "Loading..." : "Refresh"}
      </OutlineButton>
    </div>
  );

  const appendWidget = (
    <div className="flex center gap-12">
      <span className="fs-14 charcoal-80">
        Page <b>{page}</b> / <b>{totalPages}</b>
      </span>

      <button
        className="btn outline mini"
        disabled={!canPrev || loading}
        onClick={() => setOffset((p) => Math.max(0, p - pageData.limit))}
      >
        Prev
      </button>

      <button
        className="btn outline mini"
        disabled={!canNext || loading}
        onClick={() => setOffset((p) => p + pageData.limit)}
      >
        Next
      </button>
    </div>
  );

  if (loadError) {
    return <div className="flex center box fs-20">{loadError}</div>;
  }

  return (
    <div className="agents">
      <div className="box mb-32">
        <div className="flex between-center mb-16">
          <h3 className="fs-20">Agent Management</h3>
          <span className="fs-14 charcoal-80">
            Pending agents cannot process loans or onboard customers until approved.
          </span>
        </div>

        <InputField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for this action (saved to audit logs)"
        />
      </div>

      <SearchFilterContainer
        searchHint="Search agent (name, phone, email, status)"
        reloadTable={reloadTable}
        prependWidget={prependWidget}
        appendWidget={appendWidget}
      >
        <table className="box fs-20 mini">
          <thead>
            <tr>
              <th>Full name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="center-text">
                  Loading...
                </td>
              </tr>
            ) : filteredAgents.length === 0 ? (
              <tr>
                <td colSpan={5} className="center-text">
                  No agents found.
                </td>
              </tr>
            ) : (
              filteredAgents.map((a) => {
                const id = a?.id;
                const name = `${a?.firstName || ""} ${a?.lastName || ""}`.trim() || "-";
                const phone = a?.phoneNo || "-";
                const email = a?.email || "-";
                const status = a?.status;

                const isBusy = busyAgentId && busyAgentId === id;

                const canApprove = status === "pending_approval";
                const canSuspend = status !== "disabled" && status !== "suspended";
                const canUnsuspend = status === "suspended";

                return (
                  <tr key={id || `${email}-${phone}`}>
                    <td style={{ wordBreak: "break-word" }}>{name}</td>
                    <td style={{ wordBreak: "break-word" }}>{phone}</td>
                    <td style={{ wordBreak: "break-word" }}>{email}</td>
                    <td>
                      <span className={`capitalize ${statusColorClass(status)} fs-16`}>
                        {STATUS_LABELS[status] || status || "Unknown"}
                      </span>
                    </td>
                    <td>
                      <div className="flex center gap-12">
                        <button
                          className="btn outline mini"
                          disabled={!id}
                          onClick={() => navigate(`/agents/${id}/activities`)}
                        >
                          View Activities
                        </button>
                        {canApprove && (
                          <PrimaryButton
                            className="mini"
                            disabled={isBusy}
                            onClick={() => doStatusUpdate(a, "approve")}
                          >
                            {isBusy ? "Working..." : "Approve"}
                          </PrimaryButton>
                        )}

                        {canSuspend && (
                          <button
                            className="btn outline danger mini"
                            disabled={isBusy}
                            onClick={() => doStatusUpdate(a, "suspend")}
                          >
                            {isBusy ? "Working..." : "Suspend"}
                          </button>
                        )}

                        {canUnsuspend && (
                          <button
                            className="btn outline mini"
                            disabled={isBusy}
                            onClick={() => doStatusUpdate(a, "unsuspend")}
                          >
                            {isBusy ? "Working..." : "Unsuspend"}
                          </button>
                        )}

                        {!canApprove && !canSuspend && !canUnsuspend && (
                          <span className="fs-14 charcoal-60">No actions</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </SearchFilterContainer>
    </div>
  );
}
